import apiService from './api';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  department?: string;
  employeeId?: string;
  isActive: boolean;
  permissions: string[];
  createdAt: string;
  updatedAt: string;
}

export enum UserRole {
  ADMIN = 'ADMIN',
  LABORATORY_MANAGER = 'LABORATORY_MANAGER',
  SAMPLE_RECEIVER = 'SAMPLE_RECEIVER',
  TECHNICIAN_PREP = 'TECHNICIAN_PREP',
  ANALYST = 'ANALYST',
  QUALITY_MANAGER = 'QUALITY_MANAGER',
  CLIENT_VIEWER = 'CLIENT_VIEWER',
  
  // Laboratory-specific roles for SAA
  SAA_PREP_CHIMIQUE = 'SAA_PREP_CHIMIQUE',
  SAA_ANALYST = 'SAA_ANALYST',
  
  // Laboratory-specific roles for ICP
  ICP_PREP_CHIMIQUE = 'ICP_PREP_CHIMIQUE',
  ICP_ANALYST = 'ICP_ANALYST',
  
  // Laboratory-specific roles for AU
  AU_PREP_CHIMIQUE = 'AU_PREP_CHIMIQUE',
  AU_ANALYST = 'AU_ANALYST',
  
  // Laboratory-specific roles for Volumétrie
  VOLUMETRIE_PREP_CHIMIQUE = 'VOLUMETRIE_PREP_CHIMIQUE',
  VOLUMETRIE_ANALYST = 'VOLUMETRIE_ANALYST',
  
  // Mechanical preparation (shared across all laboratories)
  PREPARATION_MECANIQUE = 'PREPARATION_MECANIQUE',
  
  // Validation service
  VALIDATION_SERVICE = 'VALIDATION_SERVICE',
}

export interface CreateUserDto {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  department?: string;
  employeeId?: string;
  permissions?: string[];
}

export interface UpdateUserDto {
  email?: string;
  firstName?: string;
  lastName?: string;
  role?: UserRole;
  department?: string;
  employeeId?: string;
  isActive?: boolean;
  permissions?: string[];
}

export interface UserFilters {
  role?: UserRole[];
  department?: string;
  isActive?: boolean;
  search?: string;
}

export interface UserListResponse {
  users: User[];
  total: number;
  page: number;
  limit: number;
}

class UsersService {
  async getUsers(
    page: number = 1,
    limit: number = 20,
    filters?: UserFilters
  ): Promise<UserListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (filters?.role?.length) {
      filters.role.forEach(role => params.append('role', role));
    }
    if (filters?.department) {
      params.append('department', filters.department);
    }
    if (filters?.isActive !== undefined) {
      params.append('isActive', filters.isActive.toString());
    }
    if (filters?.search) {
      params.append('search', filters.search);
    }

    return apiService.get<UserListResponse>(`/users?${params.toString()}`);
  }

  async getTechnicians(): Promise<User[]> {
    return apiService.get<User[]>('/users/technicians');
  }

  async getAnalysts(): Promise<User[]> {
    return apiService.get<User[]>('/users/analysts');
  }

  async getUsersByRole(role: UserRole): Promise<User[]> {
    return apiService.get<User[]>(`/users/by-role/${role}`);
  }

  async getUser(id: string): Promise<User> {
    return apiService.get<User>(`/users/${id}`);
  }

  async createUser(userData: CreateUserDto): Promise<User> {
    return apiService.post<User>('/users', userData);
  }

  async updateUser(id: string, userData: UpdateUserDto): Promise<User> {
    return apiService.patch<User>(`/users/${id}`, userData);
  }

  async deleteUser(id: string): Promise<{ message: string }> {
    return apiService.delete<{ message: string }>(`/users/${id}`);
  }

  async searchUsers(query: string): Promise<User[]> {
    return apiService.get<User[]>(`/users/search?q=${encodeURIComponent(query)}`);
  }
}

export const usersService = new UsersService();
export default usersService; 